import { t } from "../i18n/index.ts";
import { languageSelect } from "../i18n/switcher.ts";

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

export function button(label: string, className: string, onClick: () => void): HTMLButtonElement {
  const b = el("button", className, label);
  b.type = "button";
  b.addEventListener("click", (e) => {
    e.stopPropagation();
    onClick();
  });
  return b;
}

/** Two-option (or more) toggle row with a clear selected (`is-on`) state. */
export function choiceGroup(items: Array<[string, boolean, () => void]>): HTMLElement {
  const wrap = el("div", "rt-choices");
  wrap.setAttribute("role", "radiogroup");
  for (const [label, on, pick] of items) {
    const b = button(on ? `✓ ${label}` : label, `rt-choice${on ? " is-on" : ""}`, pick);
    b.setAttribute("role", "radio");
    b.setAttribute("aria-checked", String(on));
    wrap.append(b);
  }
  return wrap;
}

export interface QuestBarOptions {
  crumb: string;
  status?: string;
  /** Heart control on the public quest page (save / unsave). */
  saveHeart?: HTMLElement;
  previewHref?: string;
  viewHref?: string;
  editHref?: string;
  exitHref?: string;
  /** Return false to cancel navigation (e.g. unsaved published edits). */
  onExit?: (event: MouseEvent) => void;
  /** Publish, overflow menu, etc. — inserted after language, before Exit. */
  trailing?: (bar: HTMLElement) => void;
}

/** Top bar shared by the picker, the Route Trace editor, and the quest view. */
export function questBar(opts: QuestBarOptions): HTMLElement {
  const bar = el("header", "qe-bar");
  const brand = el("a", "qe-brand");
  brand.href = opts.exitHref ?? "/";
  brand.append(el("span", "qe-logo"), el("span", "qe-brand-name", t("brand.name").toUpperCase()));
  bar.append(brand, el("span", "qe-crumb-sep", "/"));
  const crumb = el("span", "qe-crumb", opts.crumb);
  if (opts.status) crumb.append(el("span", "qe-crumb-status", ` · ${opts.status}`));
  bar.append(crumb, el("span", "qe-bar-gap"));
  if (opts.saveHeart) bar.append(opts.saveHeart);
  if (opts.viewHref) {
    const view = el("a", "qe-bar-btn", t("sheet.view"));
    view.href = opts.viewHref;
    bar.append(view);
  }
  if (opts.editHref) {
    const edit = el("a", "qe-bar-btn qe-bar-btn-primary", t("mode.edit"));
    edit.href = opts.editHref;
    bar.append(edit);
  }
  if (opts.previewHref) {
    const preview = el("a", "qe-bar-btn", t("qbar.preview"));
    preview.href = opts.previewHref;
    preview.target = "_blank";
    preview.rel = "noopener";
    bar.append(preview);
  }
  bar.append(languageSelect());
  opts.trailing?.(bar);
  const exit = el("a", "qe-bar-btn", t("qbar.exit"));
  exit.href = opts.exitHref ?? "/";
  if (opts.onExit) {
    exit.addEventListener("click", (e) => opts.onExit!(e));
  }
  bar.append(exit);
  return bar;
}
export function questWorkspace(): {
  root: HTMLElement;
  page: HTMLElement;
  editor: HTMLElement;
  palette: HTMLElement;
} {
  const root = el("div", "qe-body qe-edit-grid");
  const pageCol = el("main", "qe-page-col");
  const page = el("article", "qp");
  pageCol.append(page);
  const editor = el("section", "qe-editor-col");
  const palette = el("aside", "qe-palette-col");
  root.append(pageCol, editor, palette);
  return { root, page, editor, palette };
}

/** View-only quest page layout (preview column centered). */
export function questViewWorkspace(): { root: HTMLElement; page: HTMLElement; guide: HTMLElement } {
  const root = el("div", "qe-body");
  const label = el("aside", "qe-label", t("qpage.label"));
  const pageCol = el("main", "qe-page-col");
  const page = el("article", "qp");
  pageCol.append(page);
  const guide = el("section", "qe-guide-col");
  root.append(label, pageCol, guide);
  return { root, page, guide };
}
